/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { type AxiosInstance, type AxiosResponse, type AxiosRequestConfig } from "axios";
import CryptoJS from "crypto-js";
import i18n from '@/locales';
import { iamTeamManager, iamAccountManager, iamTokenManager, agentManager } from '@/manager';
import { API_CONFIG, getApiBaseURL, HTTP_STATUS, BUSINESS_CODE } from '../config';
import type { ApiResponse, RequestConfig } from '../types';

// 签名生成函数
const generateSign = (token: string, authTimestamp: string): string => {
  const random =
    "bK5uzLcwSX4ohFDytHxPduC68m4qeihTJIGkQ1l3ChXB3wXifwa1m6xr4vHXHLDX";
  const string = `AUTH-TIMESTAMP=${authTimestamp}&AUTH-TOKEN=${token}#${random}`;
  const md5 = CryptoJS.MD5(string).toString();
  return md5;
};

class ApiService {
  private instance: AxiosInstance;

  constructor(axiosDefaults: AxiosRequestConfig) {
    this.instance = axios.create(axiosDefaults);
    this.setupInterceptors();
  }

  /**
   * 设置拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        const token = iamTokenManager.getToken();
        const agentId = agentManager.getAgentId();
        if (token) {
          config.headers.Authorization = token;
          config.headers["AUTH-TOKEN"] = token;
          const authTimestamp = Date.now().toString();
          config.headers["AUTH-TIMESTAMP"] = authTimestamp;
          config.headers["AUTH-SIGN"] = generateSign(token, authTimestamp);
        }
        // 添加语言请求头
        config.headers['X-Language'] = i18n.language;
        // 添加请求ID用于追踪
        config.headers['X-Request-ID'] = this.generateRequestId();
        // TODO: 添加智能体ID请求头
        config.headers['X-Agent-Id'] = config.headers['X-Agent-Id'] || agentId;

        // 添加团队代码请求头
        if (!config.headers['Team-Code']) {
          if (iamAccountManager.get()) {
            config.headers['Team-Code'] =
              iamTeamManager.getTeamCode() || iamAccountManager.get()?.teamCode || agentId || '';
          }
        }

        // 打印请求日志
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params);

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse<any>) => {
        const { data, config } = response;
        const customConfig = config as AxiosRequestConfig & RequestConfig;

        // 打印响应日志
        console.log(`[API Response] ${config.method?.toUpperCase()} ${config.url}`, data);

        // 业务逻辑处理
        if (data.code === BUSINESS_CODE.SUCCESS) {
          return data;
        } else if ((config.responseType === 'blob' || data instanceof Blob)) {
          return response;
        } else {
          return this.handleBusinessError(data, customConfig);
        }
      },
      (error) => {
        return this.handleHttpError(error);
      }
    );
  }

  /**
   * 处理业务错误
   */
  private handleBusinessError(data: ApiResponse, config: RequestConfig): Promise<never> {
    const { code, message } = data;

    switch (code) {
      case BUSINESS_CODE.TOKEN_EXPIRED:
        this.handleTokenExpired();
        break;
    }
    console.error(config.errorMessage || message);
    return Promise.reject(data);
  }

  /**
   * 处理HTTP错误
   */
  private handleHttpError(error: any): Promise<never> {
    let message = 'api.errors.networkError';
    if (error.response) {
      const { status, data } = error.response;
      switch (status) {
        case HTTP_STATUS.BAD_REQUEST:
          message = data?.message || 'api.errors.badRequest';
          break;
        case HTTP_STATUS.UNAUTHORIZED:
          message = 'api.errors.unauthorized';
          this.handleTokenExpired();
          break;
        case HTTP_STATUS.FORBIDDEN:
          message = 'api.errors.forbidden';
          break;
        case HTTP_STATUS.NOT_FOUND:
          message = 'api.errors.notFound';
          break;
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          message = 'api.errors.internalServerError';
          break;
      }
    } else if (error.request) {
      message = 'api.errors.networkConnectionFailed';
    }
    // 打印错误信息
    console.error(`[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error);
    return Promise.reject(error);
  }

  /**
   * GET请求
   */
  public get<T = any>(
    url: string,
    params?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.instance.get(url, {
      params,
      ...config
    });
  }

  /**
   * POST请求
   */
  public post<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.instance.post(url, data, config);
  }

  /**
   * PUT请求
   */
  public put<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.instance.put(url, data, config);
  }

  /**
   * DELETE请求
   */
  public delete<T = any>(
    url: string,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.instance.delete(url, config);
  }

  public requestRaw<T = any>(config: AxiosRequestConfig) {
    return this.instance.request<T>(config);
  }

  /**
   * 文件上传
   */
  public upload<T = any>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.instance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  /**
   * 文件下载
   */
  public async download(url: string, filename?: string): Promise<void> {
    const response = await this.instance.get(url, {
      responseType: 'blob',
    } as any);
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * 处理token过期
   */
  private handleTokenExpired(): void {
    iamTokenManager.clear();
  }

  /**
   * 生成请求ID
   */
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

const apiClientConfig: AxiosRequestConfig = {
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  withCredentials: API_CONFIG.withCredentials,
};

const apiV2ClientConfig: AxiosRequestConfig = {
  baseURL: getApiBaseURL("v2"),
  timeout: API_CONFIG.timeout,
  withCredentials: API_CONFIG.withCredentials,
};

// v1 版本实例
export const apiService = new ApiService(apiClientConfig);

// v2 版本实例
export const apiV2Service = new ApiService(apiV2ClientConfig);
export { apiV2Service as apiV2 };

export default apiService;
